import { defineComponent as _defineComponent } from 'vue'
import {
  ref, computed, onBeforeUnmount, getCurrentInstance, onMounted,
} from 'vue';
import { DateRange } from '@/services/date-range';

import i18n from '@/plugins/i18n';
import store from '@/store/store';
import localized from '@/plugins/vue-localized-formatter/src/localized';

import MainToolbar from '@/components/views/main/MainToolbar.vue';
import DateRangePickerField from '@/components/pickers/DateRangePickerField.vue';
import { loadStoreModule, tryUnloadStoreModule, unloadStoreModule } from '@/services/module-loader';
import { performDispatchAction, performSaveAction } from '@/services/vue-utils';
import { useRouter } from 'vue-router/composables';
import CampaignManagerStore from '@/store/marketing-campaign-manager-store';
import MarketingCampaign, { MarketingCampaignStatus } from '@/grpc-api/model/marketing-campaign';
import showConfirmationDialog from '@/components/dialogs/confirmation-dialog';
import { cloneModel } from '@/model/model-utils';
import RepeatableTimer from '@/services/repeatable-timer';
import PaywallUpgradeDialog from '@/components/dialogs/PaywallUpgradeDialog.vue';
import PaywallButton from '@/components/buttons/PaywallButton.vue';
import NewsletterCampaignDetailDialog from './NewsletterCampaignDetailsDialog.vue';
import NewsletterQuotaBar from './NewsletterQuotaBar.vue';
import { colorForCampaignStatus } from './newsletter';


export default /*#__PURE__*/_defineComponent({
  __name: 'NewsletterCampaigns',
  setup(__props) {


const headers = [
  {
    text: i18n.t('label.date_created'), align: 'start', width: '160px', value: 'date',
  },
  {
    text: i18n.t('label.status'), align: 'center', value: 'status', width: '160px',
  },
  {
    text: i18n.t('label.newsletter_name'), align: 'start', value: 'title',
  },
  {
    text: i18n.t('label.recipient_count'), align: 'center', value: 'recipientCount', width: '200px',
  },
  {
    text: i18n.t('label.language'), align: 'center', value: 'locale', width: '200px',
  },
  {
    text: '', value: 'actions', width: '64px', align: 'center', sortable: false,
  },
];
const isNewsletterFeatureAllowed = computed<boolean>(() => store.getters.isNewsletterFeatureAllowed);

const router = useRouter();

const selectedMarketingCampaign = ref(undefined as undefined | MarketingCampaign);

// actions
const newCampaign = () => {
  router.push('/newsletter/new');
};

const showCampaignDetail = async (campaign: MarketingCampaign) => {
  if (campaign.status === MarketingCampaignStatus.Draft) {
    router.push(`/newsletter/${campaign.id}`);
  } else {
    const campaignToSelect = await performDispatchAction<MarketingCampaign>('getMarketingCampaign', { campaign });
    if (!campaignToSelect) return;

    selectedMarketingCampaign.value = campaignToSelect;
  }
};

// campaigns
const campaigns = computed(() => store.getters.marketingCampaignManagerCampaigns as MarketingCampaign[]);

// filter show archived
const showArchived = computed(() => store.getters.marketingCampaignManagerFilter.showArchived as Boolean);

const changeShowArchived = async (value: boolean) => {
  performDispatchAction('updateMarketingCampaignFilter', { showArchived: value });
};

// filter date range
const filterDateRange = computed(() => store.getters.marketingCampaignManagerFilter.dateRange as DateRange);

const filterDateRangeSelected = async (dateRange: DateRange) => {
  performDispatchAction('updateMarketingCampaignFilter', { dateRange });
};

// load campaign manager
const load = async () => {
  if (store.getters.isMarketingCampaignManagerLoaded) return;
  await performDispatchAction('loadMarketingCampaignManager', {});
};

const reset = async () => {
  store.dispatch('resetMarketingCampaignManager');
  tryUnloadStoreModule('serviceManager', async () => store.dispatch('resetServiceManager'));
};

// campaign actions
const archiveCampaign = async (campaign: MarketingCampaign) => {
  const action = async () => {
    const archivedCampaign = cloneModel(campaign);
    archivedCampaign.isArchived = !archivedCampaign.isArchived;
    await performSaveAction(
      undefined,
      async () => store.dispatch('archiveMarketingCampaign', { campaign: archivedCampaign }),
      { hideSuccess: true },
    );
  };

  showConfirmationDialog(
    campaign.title ?? i18n.t('title.newsletter_campaigns'),
    i18n.t('message.confirm_archive_campaign'),
    { action },
    {},
  );
};

const deleteCampaign = async (campaign: MarketingCampaign) => {
  const action = async () => {
    await performSaveAction(
      undefined,
      async () => store.dispatch('deleteMarketingCampaign', { campaign }),
      { hideSuccess: true },
    );
  };

  showConfirmationDialog(
    campaign.title ?? i18n.t('title.newsletter_campaigns'),
    i18n.t('message.confirm_delete_campaign_draft'),
    { action },
    {},
  );
};

let refreshTimer = undefined as RepeatableTimer | undefined;
const refresh = async () => {
  try {
    await store.dispatch('refreshMarketingCampaigns');
  } catch (e) {
    console.log('refresh campaigns error: ', e);
  }
};

onMounted(() => {
  // load store module
  loadStoreModule(
    getCurrentInstance()?.proxy!,
    'marketingCampaignManager',
    CampaignManagerStore,
    async () => { load(); },
  );

  // start refresh timer
  refreshTimer = new RepeatableTimer(refresh);
  refreshTimer.start(5000);
});

onBeforeUnmount(() => {
  refreshTimer?.stop();
  unloadStoreModule(getCurrentInstance()?.proxy!, 'marketingCampaignManager', async () => { reset(); });
});


return { __sfc: true,headers, isNewsletterFeatureAllowed, router, selectedMarketingCampaign, newCampaign, showCampaignDetail, campaigns, showArchived, changeShowArchived, filterDateRange, filterDateRangeSelected, load, reset, archiveCampaign, deleteCampaign, refreshTimer, refresh, localized, MainToolbar, DateRangePickerField, MarketingCampaignStatus, NewsletterCampaignDetailDialog, NewsletterQuotaBar, colorForCampaignStatus }
}

})